import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = { section_type: "text", title: "", content: "", stat_value: "", link_url: "", order: 0 };

export default function CompanyProfileSectionFormDialog({ open, onOpenChange, section, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!section;

  useEffect(() => {
    if (open) {
      setForm(
        section
          ? {
              section_type: section.section_type, title: section.title, content: section.content || "",
              stat_value: section.stat_value || "", link_url: section.link_url || "", order: section.order || 0,
            }
          : EMPTY
      );
      setFile(null);
    }
  }, [open, section]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (form.section_type === "file" && !isEdit && !file) {
      toast.error("A file is required for a File section");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (!isEdit) fd.append("section_type", form.section_type);
      fd.append("title", form.title.trim());
      fd.append("content", form.content || "");
      fd.append("stat_value", form.stat_value || "");
      fd.append("link_url", form.link_url || "");
      fd.append("order", form.order ?? 0);
      if (file) fd.append("file", file);
      if (isEdit) {
        await api.put(`/company-profile/${section.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Section updated");
      } else {
        await api.post("/company-profile", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Section added");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="company-profile-section-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Section" : "Add Section"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {!isEdit && (
            <div className="space-y-1.5">
              <Label>Section Type</Label>
              <Select value={form.section_type} onValueChange={(v) => setForm({ ...form, section_type: v })}>
                <SelectTrigger data-testid="section-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text Block</SelectItem>
                  <SelectItem value="stat">Key Stat / Revenue Figure</SelectItem>
                  <SelectItem value="file">File (Pitch Deck, etc.)</SelectItem>
                  <SelectItem value="link">Social / External Link</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input data-testid="section-title-input" value={form.title} onChange={set("title")} placeholder="e.g. Our Mission" />
          </div>
          {form.section_type === "text" && (
            <div className="space-y-1.5">
              <Label>Content</Label>
              <Textarea data-testid="section-content-input" value={form.content} onChange={set("content")} rows={4} />
            </div>
          )}
          {form.section_type === "stat" && (
            <div className="space-y-1.5">
              <Label>Value</Label>
              <Input data-testid="section-stat-input" value={form.stat_value} onChange={set("stat_value")} placeholder="e.g. ₹12Cr+ or 250+ Clients" />
            </div>
          )}
          {form.section_type === "link" && (
            <div className="space-y-1.5">
              <Label>URL</Label>
              <Input data-testid="section-link-input" value={form.link_url} onChange={set("link_url")} placeholder="https://linkedin.com/company/envirowealth" />
            </div>
          )}
          {form.section_type === "file" && (
            <div className="space-y-1.5">
              <Label>{isEdit ? "Replace File (optional)" : "File"}</Label>
              <Input data-testid="section-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Display Order</Label>
            <Input data-testid="section-order-input" type="number" value={form.order} onChange={set("order")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="section-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="section-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Add Section"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
